import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase, subscribeToRealtimeTopic } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

// ============================================
// CHAT TOPIC
// ============================================

/** React-query key of a chat topic (shared by the chat and the typing indicator). */
export function chatTopicQueryKey(side: 'guest' | 'landlord', bookingId: string | undefined | null) {
  return ['chat-topic', side, bookingId] as const;
}

/**
 * Ask the server for a chat's topic again. A group conversation's topic
 * ROTATES when a booking joins or leaves the group — call this on every resync
 * (wake-up / reconnect / poll); an unchanged topic keeps the subscription.
 */
export function refreshChatTopic(
  queryClient: QueryClient,
  side: 'guest' | 'landlord',
  bookingId: string | undefined | null,
): Promise<void> {
  if (!bookingId) return Promise.resolve();
  return queryClient.invalidateQueries({ queryKey: chatTopicQueryKey(side, bookingId) });
}

/** Hook form of refreshChatTopic: `const refreshTopic = useRefreshChatTopic('landlord'); refreshTopic(bookingId)`. */
export function useRefreshChatTopic(side: 'guest' | 'landlord') {
  const queryClient = useQueryClient();
  return useCallback(
    (bookingId: string | undefined | null) => refreshChatTopic(queryClient, side, bookingId),
    [queryClient, side],
  );
}

/**
 * The private realtime topic of a booking's chat for one side (see
 * subscribeToRealtimeTopic). The guest gets it with the link token (+ access-code
 * proof), the host only for their own bookings — so it waits for the login
 * token. null until known / when refused.
 *
 * Re-checked every few minutes and on focus / reconnect (a group's topic
 * rotates when its membership changes); refreshChatTopic forces it.
 */
export function useChatTopic(bookingId: string | undefined | null, side: 'guest' | 'landlord'): string | null {
  const { isReady } = useAuthReady();
  const { data } = useQuery({
    queryKey: chatTopicQueryKey(side, bookingId),
    queryFn: async () => {
      const { data: topic, error } = await supabase.rpc(
        side === 'guest' ? 'get_guest_chat_topic' : 'get_landlord_chat_topic',
        { p_booking_id: bookingId },
      );
      if (error) throw error;
      return typeof topic === 'string' && topic ? topic : null;
    },
    enabled: !!bookingId && (side === 'guest' || isReady),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
  return data ?? null;
}

// ============================================
// TYPING INDICATOR
// ============================================

/**
 * Hook for managing typing indicators
 * Supports both object and positional arguments for backwards compatibility
 */
export function useTypingIndicator(
  bookingIdOrOptions: string | { bookingId: string | undefined; userType: 'landlord' | 'guest'; userId?: string },
  userTypeArg?: 'landlord' | 'guest',
  userIdArg?: string
) {
  // Support both calling conventions
  const bookingId = typeof bookingIdOrOptions === 'string' ? bookingIdOrOptions : bookingIdOrOptions?.bookingId;
  const userType = typeof bookingIdOrOptions === 'string' ? userTypeArg! : bookingIdOrOptions?.userType;
  const userId = typeof bookingIdOrOptions === 'string' ? userIdArg : bookingIdOrOptions?.userId;

  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What we last told the other side ("typing" in which chat) — so switching to
  // another conversation can say "stopped" in the old one.
  const lastSentRef = useRef<{ bookingId: string; isTyping: boolean; at: number } | null>(null);
  const topic = useChatTopic(bookingId, userType);

  // The other side's typing arrives on our own topic (sent by the database).
  useEffect(() => {
    // A new conversation starts without the previous one's "piše..." (it used
    // to stick for up to 10 s after switching).
    setIsOtherTyping(false);
    if (!topic) return;

    const unsubscribe = subscribeToRealtimeTopic(topic, ({ event, payload }) => {
      if (event !== 'typing' || payload.user_type === userType) return;
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (payload.is_typing) {
        setIsOtherTyping(true);
        // Auto-clear if the "stopped typing" event never comes
        typingTimeoutRef.current = setTimeout(() => {
          setIsOtherTyping(false);
        }, 10000);
      } else {
        setIsOtherTyping(false);
      }
    });

    return () => {
      unsubscribe();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      setIsOtherTyping(false);
    };
  }, [topic, userType]);

  // Leaving a conversation (switch / unmount): drop a pending debounced send and
  // tell the old chat we stopped typing.
  useEffect(() => {
    return () => {
      if (sendTimeoutRef.current) {
        clearTimeout(sendTimeoutRef.current);
        sendTimeoutRef.current = null;
      }
      const last = lastSentRef.current;
      if (bookingId && last && last.bookingId === bookingId && last.isTyping) {
        lastSentRef.current = { bookingId, isTyping: false, at: Date.now() };
        void Promise.resolve(
          supabase.rpc('upsert_typing_indicator', {
            p_booking_id: bookingId,
            p_user_type: userType,
            p_user_id: userId || null,
            p_is_typing: false,
          }),
        ).catch(() => undefined);
      }
    };
  }, [bookingId, userType, userId]);

  // Send typing indicator (debounced)
  const sendTypingIndicator = useCallback(
    async (isTyping: boolean) => {
      if (!bookingId) return;

      // Clear existing timeout
      if (sendTimeoutRef.current) {
        clearTimeout(sendTimeoutRef.current);
      }

      // Debounce sending
      sendTimeoutRef.current = setTimeout(async () => {
        sendTimeoutRef.current = null;
        // Nothing new to say ("stopped" when we never said "typing").
        const last = lastSentRef.current;
        const sameChat = !!last && last.bookingId === bookingId;
        if (!isTyping && !(sameChat && last!.isTyping)) return;
        // Still typing: the other side keeps the indicator for 10 s — one
        // refresh every few seconds is enough (was one RPC per pause).
        if (isTyping && sameChat && last!.isTyping && Date.now() - last!.at < 3000) return;
        lastSentRef.current = { bookingId, isTyping, at: Date.now() };
        try {
          await supabase.rpc('upsert_typing_indicator', {
            p_booking_id: bookingId,
            p_user_type: userType,
            p_user_id: userId || null,
            p_is_typing: isTyping,
          });
        } catch (error) {
          console.error('Error sending typing indicator:', error);
        }
      }, 300);
    },
    [bookingId, userType, userId]
  );

  // setTyping function - main API for ChatThread
  const setTyping = useCallback((isTyping: boolean) => {
    sendTypingIndicator(isTyping);
  }, [sendTypingIndicator]);

  // Legacy API - start typing
  const startTyping = useCallback(() => {
    sendTypingIndicator(true);
  }, [sendTypingIndicator]);

  // Legacy API - stop typing
  const stopTyping = useCallback(() => {
    sendTypingIndicator(false);
  }, [sendTypingIndicator]);

  return {
    isOtherTyping,
    setTyping,
    startTyping,
    stopTyping,
  };
}

// ============================================
// REPLY TO MESSAGE
// ============================================

interface ReplyToState {
  id: string;
  content: string;
  senderType: 'landlord' | 'guest';
}

/**
 * Hook for managing reply-to functionality
 * Works without requiring messages array - ChatThread manages the reply state directly
 */
export function useReply() {
  const [replyTo, setReplyTo] = useState<ReplyToState | null>(null);

  const clearReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  return {
    replyTo,
    setReplyTo,
    clearReply,
  };
}

// ============================================
// DELETE MESSAGE
// ============================================

/**
 * Hook for deleting messages
 */
export function useDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      userType,
      userId,
    }: {
      messageId: string;
      userType: 'landlord' | 'guest';
      userId?: string;
    }) => {
      const { data, error } = await supabase.rpc('soft_delete_message', {
        p_message_id: messageId,
        p_user_type: userType,
        p_user_id: userId || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate messages queries
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['booking-messages'] });
    },
  });
}

// ============================================
// CHAT IMAGE UPLOAD
// ============================================

/** Raw file size we still try to shrink (phone photos are 3-15 MB). */
export const CHAT_IMAGE_MAX_RAW_BYTES = 25 * 1024 * 1024;
/** What may actually be uploaded after compression. */
const CHAT_IMAGE_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export type ChatImageUploadError =
  | 'not_image'     // not an image at all
  | 'too_large'     // over CHAT_IMAGE_MAX_RAW_BYTES (or still too big after shrinking)
  | 'unsupported'   // an image this browser can't decode (e.g. HEIC on Android/desktop Chrome)
  | 'upload_failed'; // storage refused / network

export type ChatImageUploadResult =
  | { url: string; error?: undefined }
  | { url: null; error: ChatImageUploadError };

class ChatImageError extends Error {
  code: ChatImageUploadError;
  constructor(code: ChatImageUploadError) {
    super(code);
    this.code = code;
  }
}

/** i18n text for an upload error (shared by the guest chat and the host thread). */
export function chatImageErrorMessage(code: ChatImageUploadError, t: (key: string, defaultValue: string) => string): string {
  switch (code) {
    case 'not_image':
      return t('chat.upload.notImage', 'Odabrana datoteka nije slika.');
    case 'too_large':
      return t('chat.upload.tooLarge', 'Slika je prevelika (najviše 25 MB).');
    case 'unsupported':
      return t('chat.upload.unsupported', 'Ovaj format slike nije podržan (npr. HEIC). Pošaljite JPG ili PNG fotografiju.');
    default:
      return t('chat.upload.failed', 'Slanje slike nije uspjelo. Provjerite vezu i pokušajte ponovo.');
  }
}

/**
 * Hook for uploading images in chat.
 *
 * `uploadImageDetailed` says why an upload failed; `uploadImage` keeps the old
 * `string | null` shape. The photo is shrunk FIRST (max 1200 px JPEG) and only
 * then size-checked — the old "max 5 MB" check before compression refused most
 * phone photos that would have been ~200 KB after shrinking.
 */
export function useChatImageUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastError, setLastError] = useState<ChatImageUploadError | null>(null);

  const uploadImageDetailed = useCallback(async (file: File, bookingId: string): Promise<ChatImageUploadResult> => {
    setIsUploading(true);
    setUploadProgress(0);
    setLastError(null);

    try {
      // Some pickers give HEIC/HEIF files an empty type — try to decode those.
      const looksLikeImage = file.type.startsWith('image/') || (!file.type && /\.(heic|heif|jpe?g|png|webp|gif|avif|bmp)$/i.test(file.name));
      if (!looksLikeImage) throw new ChatImageError('not_image');
      if (file.size > CHAT_IMAGE_MAX_RAW_BYTES) throw new ChatImageError('too_large');

      setUploadProgress(10);
      const compressedFile = await compressImage(file);
      if (compressedFile.size > CHAT_IMAGE_MAX_UPLOAD_BYTES) throw new ChatImageError('too_large');

      // compressImage always produces a JPEG; the server only accepts a plain
      // "<time>-<random>.<ext>" name (no spaces / non-ASCII from the original file).
      const filename = `chat/${bookingId}/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;

      // Upload to Supabase Storage
      setUploadProgress(40);
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filename, compressedFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg',
        });

      if (uploadError) throw new ChatImageError('upload_failed');

      setUploadProgress(90);

      // Get public URL
      const { data: urlData } = supabase.storage.from('images').getPublicUrl(filename);

      setUploadProgress(100);
      return { url: urlData.publicUrl };
    } catch (error) {
      const code: ChatImageUploadError = error instanceof ChatImageError ? error.code : 'upload_failed';
      if (import.meta.env.DEV) console.error('Error uploading chat image:', error);
      setLastError(code);
      return { url: null, error: code };
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, []);

  const uploadImage = useCallback(
    async (file: File, bookingId: string): Promise<string | null> => (await uploadImageDetailed(file, bookingId)).url,
    [uploadImageDetailed],
  );

  return {
    uploadImage,
    uploadImageDetailed,
    lastError,
    isUploading,
    uploadProgress,
  };
}

const COMPRESS_MAX_SIZE = 1200;

/** Decode an image file into something drawable; 'unsupported' when the browser can't. */
async function decodeImage(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  // createImageBitmap: no data-URL copy of a 10 MB photo in memory, and it
  // applies the EXIF orientation (portrait phone photos came out sideways).
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      // Fall back to <img> (older Safari can't take options / some formats).
    }
  }
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new ChatImageError('unsupported'));
      el.src = objectUrl;
    });
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      close: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (err) {
    URL.revokeObjectURL(objectUrl);
    throw err instanceof ChatImageError ? err : new ChatImageError('unsupported');
  }
}

// Compress image helper: max 1200 px on the long side, JPEG 80 %.
async function compressImage(file: File): Promise<File> {
  const decoded = await decodeImage(file);
  try {
    let { width, height } = decoded;
    if (!width || !height) throw new ChatImageError('unsupported');
    if (width > height && width > COMPRESS_MAX_SIZE) {
      height = Math.round((height * COMPRESS_MAX_SIZE) / width);
      width = COMPRESS_MAX_SIZE;
    } else if (height > COMPRESS_MAX_SIZE) {
      width = Math.round((width * COMPRESS_MAX_SIZE) / height);
      height = COMPRESS_MAX_SIZE;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ChatImageError('upload_failed');
    // Transparent PNGs would turn black in a JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(decoded.source, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
    if (!blob) throw new ChatImageError('unsupported');
    return new File([blob], 'image.jpg', { type: 'image/jpeg' });
  } finally {
    decoded.close();
  }
}

// ============================================
// NOTIFICATION SOUND
// ============================================

// Create audio context lazily for generating notification sound
let audioContext: AudioContext | null = null;

/**
 * Hook for playing notification sounds using Web Audio API
 * Generates a pleasant "ding" sound programmatically
 */
export function useNotificationSound() {
  const playSound = useCallback(() => {
    try {
      // Create audio context lazily (must be after user interaction)
      if (!audioContext) {
        audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
      }

      // Resume context if suspended (iOS requirement)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const now = audioContext.currentTime;

      // Create oscillator for the "ding" sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      // Connect nodes
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure oscillator - pleasant notification tone
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now); // A5 note
      oscillator.frequency.exponentialRampToValueAtTime(440, now + 0.1); // Glide down to A4

      // Configure gain envelope (quick attack, medium decay)
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Quick attack
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3); // Decay

      // Play the sound
      oscillator.start(now);
      oscillator.stop(now + 0.3);

      // Optional: Add a second harmonic for richer sound
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      oscillator2.type = 'sine';
      oscillator2.frequency.setValueAtTime(1320, now); // E6 - fifth above
      oscillator2.frequency.exponentialRampToValueAtTime(660, now + 0.1);
      gainNode2.gain.setValueAtTime(0, now);
      gainNode2.gain.linearRampToValueAtTime(0.15, now + 0.01);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
      oscillator2.start(now);
      oscillator2.stop(now + 0.25);

    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, []);

  return { playSound };
}

// ============================================
// EMOJI PICKER DATA
// ============================================

export const EMOJI_CATEGORIES = [
  {
    name: 'smileys',
    label: '😀',
    emojis: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳'],
  },
  {
    name: 'gestures',
    label: '👋',
    emojis: ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏'],
  },
  {
    name: 'hearts',
    label: '❤️',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝'],
  },
  {
    name: 'objects',
    label: '🏠',
    emojis: ['🏠', '🏡', '🏢', '🏨', '🏖️', '🏝️', '⛱️', '🚗', '🚕', '🚌', '✈️', '🛫', '🛬', '🧳', '🛏️', '🛁', '🚿', '🔑', '🗝️', '📱', '💻', '📷', '🎥', '📺', '💡', '🔦', '💰', '💳'],
  },
  {
    name: 'food',
    label: '🍕',
    emojis: ['🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🥗', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🍤', '🍙', '🍚', '🍘', '🍥', '🥮', '🍢', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '☕', '🍵', '🍺', '🍻', '🥂', '🍷'],
  },
  {
    name: 'weather',
    label: '☀️',
    emojis: ['☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️', '❄️', '💨', '🌊', '🌈', '🌅', '🌄', '🌇', '🌆', '🌃', '🌉'],
  },
];

export type EmojiCategory = typeof EMOJI_CATEGORIES[number];
